import type { FpMessagePart } from "../../../../../schemas/codegen";
import { Markdown } from "../../Markdown";
import { TypingIndicator } from "../../TypingIndicator";
import { AddSecrets } from "./AddSecrets";
import { CreateFile } from "./CreateFile";
import { Deploy } from "./Deploy";
import { EndpointTools } from "./EndpointTools";
import { GenericTool } from "./GenericTool";
import { InsertText } from "./InsertText";
import { LookUpKnowledge } from "./LookUpKnowledge";
import { McpTools } from "./McpTools";
import { StringReplace } from "./StringReplace";
import { SystemMessage } from "./SystemMessage";
import { TaskManagement } from "./TaskManagement";
import { Typecheck } from "./Typecheck";
import { UnsupportedOperation } from "./UnsupportedOperation";
import { ViewText } from "./ViewText";
import { WebSearch } from "./WebSearch";

interface MessagePartsProps {
  parts: FpMessagePart[];
  messageId: string;
}

const syntaxHighlighterOptions = {
  className: "rounded-md p-4 bg-muted",
};

function TextPart({
  part,
}: { part: Extract<FpMessagePart, { type: "text" }> }) {
  return (
    <div className="overflow-x-auto">
      <Markdown
        syntaxHighlighterOptions={syntaxHighlighterOptions}
        isStreaming={part.status === "streaming"}
      >
        {part.content}
      </Markdown>
      {part.status === "streaming" && !part.content && <TypingIndicator />}
    </div>
  );
}

function ToolCallPart({
  part,
  messageId,
}: {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
  messageId: string;
}) {
  switch (part.toolName) {
    case "createFile":
      return <CreateFile part={part} messageId={messageId} />;
    case "deploy":
      return <Deploy part={part} messageId={messageId} />;
    case "str_replace_editor": {
      const command = part.args?.command as string;
      if (command === "str_replace") {
        return <StringReplace part={part} messageId={messageId} />;
      }
      if (command === "insert") {
        return <InsertText part={part} messageId={messageId} />;
      }
      if (command === "view") {
        return <ViewText part={part} messageId={messageId} />;
      }
      return <UnsupportedOperation part={part} />;
    }
    case "addSecrets":
      return <AddSecrets part={part} />;
    case "typeCheck":
      return <Typecheck part={part} messageId={messageId} />;
    case "addMcpServer":
    case "removeMcpServer":
    case "listMcpServers":
      return <McpTools part={part} />;
    case "listEndpoints":
    case "fetchEndpoint":
      return <EndpointTools part={part} />;
    case "webSearch":
      return <WebSearch part={part} />;
    case "lookUpKnowledge":
      return <LookUpKnowledge part={part} />;
    case "system":
      return <SystemMessage part={part} />;
    case "createTasks":
    case "updateTask":
    case "getTasks":
    case "getNextTask":
    case "checkTasksCompleted":
    case "getTaskStats":
      return <TaskManagement part={part} />;
    default:
      return <GenericTool part={part} />;
  }
}

export function MessageParts({ parts, messageId }: MessagePartsProps) {
  return (
    <>
      {parts?.map((part) => {
        // HACK - I haven't tracked down why, it's usually during failure modes when a message is interrupted for whatever reason, but sometimes parts lack IDs on the frontend (fixed after refresh)
        if (!part?.id) {
          console.error(
            "[MessageParts.tsx] part has no id - something is wrong",
            { part },
          );
          return null;
        }
        switch (part?.type) {
          case "text":
            return <TextPart key={part.id} part={part} />;

          case "tool-call":
            return (
              <div
                key={part.id}
                className="animate-in fade-in-0 duration-700 min-w-0"
              >
                <ToolCallPart part={part} messageId={messageId} />
              </div>
            );

          default:
            return null;
        }
      })}
    </>
  );
}
